import { HttpClient, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IAudioProcessorTask, IAudioProcessorTaskBlock } from '../models/audio-processor-task.model';
import { IAcusticaSubscription } from '../models/acustica-subscription.model';
import { IAudioPlugin } from '../models/plugin.model';
import { IUser, User } from '../models/acustica-user.model';
import { ITaskSettings } from '../models/task-settings.model';
import { IUploadUrlResponse } from '../models/upload-url-response.model';
const endpoints = environment.endpoints;

@Injectable({
  providedIn: 'root'
})

export class DaoService {

  constructor(private http: HttpClient) {

  }

  public GetPlugins(): Observable<IAudioPlugin[]> {
    var endpoint = endpoints['Luigi.Service'] + "/AudioProcessing/Plugins";
    return this.http.get<IAudioPlugin[]>(endpoint);
  }

  public SetPlugins(plugins: IAudioPlugin[]): Observable<void> {
    var endpoint = endpoints['Luigi.Service'] + "/Acustica/Plugins";
    return this.http.post<any>(endpoint, plugins);
  }

  public PersistAndStart(sessionIds: any, taskId: string): Observable<any> {
    let endpoint = endpoints['Luigi.Service'] + "/File/PersistAndStart/" + taskId;
    return this.http.post(endpoint, sessionIds);
  }

  public GetUploadUrl(fileName: string, taskId: string, blockId: string): Observable<IUploadUrlResponse> {
    var endpoint = endpoints['Luigi.Service'] + "/File/UploadUrl?fileName=" + fileName + "&taskId=" + taskId + "&blockId=" + blockId;
    return this.http.get<IUploadUrlResponse>(endpoint);
  }

  public GetUploadStatus(sessionId: string): Observable<any> {
    let endpoint = endpoints['Luigi.Service'] + "/File/Upload/Status/" + sessionId;
    return this.http.get(endpoint);
  }

  public CreateUploadSession(file: any, chunkSize: number, userId: string): Observable<any> {
    let endpoint = endpoints['Luigi.Service'] + "/File/Create/" + userId;
    let payload = {
      chunkSize: chunkSize,
      totalSize: file.size,
      fileName: file.fileName,
    }
    return this.http.post(endpoint, payload);
  }

  public UploadFile(file: File, taskId: string, blockId: string): Observable<any> {
    var endpoint = endpoints['Luigi.Service'] + "/File/Upload";
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append("taskId", taskId);
    formData.append("blockId", blockId);

    return this.http.post(endpoint, formData, { reportProgress: true, observe: 'events', responseType: "json"});
  }

  public UploadFileToUrlBackblaze(file: File, urlResponse: IUploadUrlResponse, userId: string, taskId: string, blockId: string): Observable<any> {
    var endpoint = urlResponse.UploadUrl;
    const formData = new FormData();
    let headers: any = {};
    formData.append('file', file, file.name);
    headers["X-Bz-Content-Sha1"] = "do_not_verify";
    headers["X-Bz-File-Name"] = encodeURI(userId + "/" + taskId + "/" + blockId + "/" + file.name);
    headers["Authorization"] = urlResponse.AuthorizationToken;

    return this.http.post(endpoint, file, {
      reportProgress: true,
      observe: 'events',
      responseType: "json",
      headers: headers
    });
  }

  public UploadFileToUrlAmazon(file: File, urlResponse: string, userId: string, taskId: string, blockId: string): Observable<any> {
    var endpoint = urlResponse;
    const formData = new FormData();
    const headers: any = {
      "Content-Type": "application/octet-stream"
    }
    formData.append('file', file, file.name);

    return this.http.put(endpoint, file, {
      reportProgress: true,
      observe: 'events',
      responseType: "json",
      headers: headers
    });
  }

  public UploadFileChunk(file: File, taskId: string, blockId: string, chunkId: string): Observable<any> {
    var endpoint = endpoints['Luigi.Service'] + "/File/Upload";
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append("taskId", taskId);
    formData.append("blockId", blockId);
    formData.append("chunkId", chunkId);

    return this.http.post(endpoint, formData, { reportProgress: true, observe: 'events', responseType: "json" });
  }

  public previewBlock(block: IAudioProcessorTaskBlock, file: File) {
    var endpoint = endpoints['Luigi.Service'] + "/File/Preview";
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append("serializedBlock", JSON.stringify(block));

    return this.http.post(endpoint, formData, { observe: 'events', responseType: "arraybuffer" });
  }

  public UploadFiles(files: File[], taskId: string): Observable<any> {
    var endpoint = endpoints['Luigi.Service'] + "/File/UploadAndStart";
    const formData = new FormData();
    for (var f in files) {
      let file = files[f];
      formData.append('files', file, file.name);
    }
    formData.append("taskId", taskId);

    return this.http.post(endpoint, formData, { reportProgress: true, observe: 'events', responseType: "json" });
  }

  public GetTask(id: string): Observable<IAudioProcessorTask> {
    var endpoint = endpoints['Luigi.Service'] + "/AudioProcessing/Task?id=" + id;

    return this.http.get<IAudioProcessorTask>(endpoint);
  }

  public GetCurrentUserTasks(): Observable<IAudioProcessorTask[]> {
    var endpoint = endpoints['Luigi.Service'] + "/AudioProcessing/TasksByUserId";

    return this.http.get<IAudioProcessorTask[]>(endpoint);
  }

  public GetTasksByUserId(userId: string): Observable<IAudioProcessorTask[]> {
    var endpoint = endpoints['Luigi.Service'] + "/Acustica/Tasks?userId=" + userId;

    return this.http.get<IAudioProcessorTask[]>(endpoint);
  }

  public AddTask(task: IAudioProcessorTask): Observable<IAudioProcessorTask> {
    var endpoint = endpoints['Luigi.Service'] + "/AudioProcessing/Task";

    return this.http.post<IAudioProcessorTask>(endpoint, task);
  }

  public UpdateTask(task: IAudioProcessorTask): Observable<IAudioProcessorTask> {
    var endpoint = endpoints['Luigi.Service'] + "/AudioProcessing/Task";

    return this.http.patch<IAudioProcessorTask>(endpoint, task);
  }

  public DeleteTask(taskId: string): Observable<any> {
    var endpoint = endpoints['Luigi.Service'] + "/AudioProcessing/Task?taskId=" + taskId;

    return this.http.delete(endpoint);
  }

  public UpdateTaskBlock(taskBlock: IAudioProcessorTaskBlock): Observable<any> {
    var endpoint = endpoints['Luigi.Service'] + "/AudioProcessing/TaskBlock";

    return this.http.patch(endpoint, taskBlock);
  }

  public QueueTask(taskId: string): Observable<any> {
    var endpoint = endpoints['Luigi.Worker.Sevice'] + "/Task/Queue/" + taskId;

    return this.http.post(endpoint, null);
  }

  public RunTask(taskId: string): Observable<any> {
    var endpoint = endpoints['Luigi.Worker.Sevice'] + "/Task/Run";

    return this.http.post(endpoint, taskId);
  }

  public GetDownloadLink(taskId: string): Observable<any> {
    var endpoint = endpoints['Luigi.Service'] + "/File/Download?taskId=" + taskId;

    return this.http.get(endpoint);
  }

  public SyncOrders(): Observable<any> {
    var endpoint = endpoints['Luigi.Service'] + "/Acustica/Orders";

    return this.http.post(endpoint, null);
  }

  public GetSubscription(): Observable<IAcusticaSubscription> {
    var endpoint = endpoints['Luigi.Service'] + "/Acustica/Subscription";

    return this.http.get<IAcusticaSubscription>(endpoint);
  }

  public GetSlots(): Observable<any[]> {
    var endpoint = endpoints['Luigi.Service'] + "/Acustica/Slots";

    return this.http.get<any[]>(endpoint);
  }

  public SyncSlots(): Observable<void> {
    var endpoint = endpoints['Luigi.Service'] + "/Acustica/Slots";

    return this.http.post<void>(endpoint, null);
  }

  public SetUser(): Observable<any> {
    var endpoint = endpoints['Luigi.Service'] + "/Acustica/User";

    return this.http.post<IUser>(endpoint, null);
  }

  public GetUser(): Observable<any> {
    var endpoint = endpoints['Luigi.Service'] + "/Acustica/User";

    return this.http.get<IUser>(endpoint);
  }

  public SetUserEmailNotifications(enableNotifications: boolean): Observable<any> {
    var endpoint = endpoints['Luigi.Service'] + "/Acustica/EmailNotifications";

    return this.http.post<IUser>(endpoint, enableNotifications);
  }

  public GetTaskSettings(): Observable<ITaskSettings> {
    var endpoint = endpoints['Luigi.Service'] + "/Acustica/TaskSettings";

    return this.http.get<ITaskSettings>(endpoint);
  }

  public SetTaskSettings(settings: ITaskSettings): Observable<void> {
    var endpoint = endpoints['Luigi.Service'] + "/Acustica/TaskSettings";

    return this.http.post<any>(endpoint, settings);
  }

  public GetRealmAuthorization(): Observable<any> {
    var endpoint = endpoints['Luigi.Service'] + "/Acustica/RealmAuthorization";

    return this.http.get<any>(endpoint);
  }

  public GetUsers(): Observable<IUser[]> {
    var endpoint = endpoints['Luigi.Service'] + "/Acustica/Users";

    return this.http.get<any>(endpoint);
  }

  public SetUsers(users: IUser[]): Observable<void> {
    var endpoint = endpoints['Luigi.Service'] + "/Acustica/Users";

    return this.http.post<any>(endpoint, users);
  }

  public GetEmailSettings(): Observable<any> {
    var endpoint = endpoints['Luigi.Service'] + "/Acustica/EmailSettings";

    return this.http.get<any>(endpoint);
  }

  public SetEmailSettings(emailConfiguration: any): Observable<void> {
    var endpoint = endpoints['Luigi.Service'] + "/Acustica/EmailSettings";

    return this.http.post<any>(endpoint, emailConfiguration);
  }

}

